/**
 * Auth API Service
 * Handles communication with backend for custom email verification flow.
 * Uses public/unauthenticated endpoints.
 */

import { createLogger } from '@/lib/logger';
import { PUBLIC_API_URL } from '@/lib/apiConfig';

const log = createLogger('AuthApi');

export interface SendCodeRequest {
  email: string;
}

export interface SendCodeResponse {
  success: boolean;
}

export interface VerifyCodeRequest {
  email: string;
  code: string;
}

export interface VerifyCodeResponse {
  verified: boolean;
}

class AuthApiService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Send a 6-digit verification code to the given email.
   */
  async sendVerificationCode(email: string): Promise<SendCodeResponse> {
    log.debug('Sending verification code', { email });

    const response = await fetch(`${this.baseUrl}/auth/send-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const errorMsg = data.error || `HTTP ${response.status}`;
      log.error('Failed to send verification code', new Error(errorMsg));
      throw new Error(errorMsg);
    }

    const data = await response.json();
    log.info('Verification code sent', { email });
    return data;
  }

  /**
   * Verify a 6-digit code for the given email.
   */
  async verifyCode(email: string, code: string): Promise<VerifyCodeResponse> {
    log.debug('Verifying code', { email });

    const response = await fetch(`${this.baseUrl}/auth/verify-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const errorMsg = data.error || `HTTP ${response.status}`;
      log.error('Failed to verify code', new Error(errorMsg));
      throw new Error(errorMsg);
    }

    const data = await response.json();
    log.info('Code verified', { email });
    return data;
  }
}

// Export singleton instance
export const authApi = new AuthApiService(PUBLIC_API_URL);
